// pages/formularios/RespostasLista.js — as respostas de um formulário, legíveis.
//
// Out/26, Formulários PR 2. O mesmo desenho em quatro lugares: a bolha do
// chat, o painel do contato (CRM), a aba Respostas e o teste do construtor.
// Cada lugar tem a sua paleta (o chat usa `--ch-*`, o CRM o CRM_THEME, a
// tela de Formulários `--fm-*`), então as cores chegam por `cores` e o
// componente só cuida da forma: pergunta em cima, resposta embaixo, anexos
// como miniatura, e o que a resposta fez no CRM no rodapé.
//
// O rodapé diz a diferença EXATA que o servidor gravou (`crm`): o que foi
// preenchido, o que ficou como estava porque o CRM já tinha outro valor
// ("respondeu diferente do CRM" — alguém decide), e a pasta. No teste,
// nada foi gravado, e o rodapé diz o que aconteceria.

import { Box, Typography } from '@mui/material';
import { FiAlertCircle, FiCheck, FiFolder, FiPaperclip } from 'react-icons/fi';

const BASE = process.env.REACT_APP_API_URL || '';
const urlDe = (u) => (!u ? null : u.startsWith('http') ? u : `${BASE}${u}`);

const ehImagem = (a) => (a.mime || '').startsWith('image/') || /\.(jpe?g|png|webp|gif)$/i.test(a.url || '');

function Anexos({ itens, cores }) {
  return (
    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 0.5 }}>
      {itens.map((a, k) => {
        const url = urlDe(a.url);
        if (url && ehImagem(a)) {
          return (
            <Box
              key={k} component="a" href={url} target="_blank" rel="noopener noreferrer"
              aria-label={a.nome ? `Abrir ${a.nome}` : 'Abrir foto'}
              sx={{ display: 'block', width: 64, height: 64, borderRadius: '8px', overflow: 'hidden', border: `1px solid ${cores.borda}`, bgcolor: cores.fundo }}
            >
              <Box component="img" src={url} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
            </Box>
          );
        }
        return (
          <Box
            key={k} component={url ? 'a' : 'span'} href={url || undefined} target="_blank" rel="noopener noreferrer"
            sx={{
              display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.5, borderRadius: '8px',
              border: `1px solid ${cores.borda}`, fontSize: '0.74rem', color: url ? cores.texto : cores.texto3,
              textDecoration: 'none', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}
          >
            <FiPaperclip size={12} /> {a.nome || 'Arquivo'}{!url && ' (não baixado)'}
          </Box>
        );
      })}
    </Box>
  );
}

function Linha({ icone, cor, children }) {
  return (
    <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-start', fontSize: '0.74rem', lineHeight: 1.45, color: cor }}>
      <Box component="span" sx={{ mt: '2px', flexShrink: 0, display: 'inline-flex' }}>{icone}</Box>
      <Box component="span" sx={{ minWidth: 0, wordBreak: 'break-word' }}>{children}</Box>
    </Box>
  );
}

export function ResumoCrm({ crm, teste, cores }) {
  if (!crm) return null;
  const preenchidos = crm.preenchidos || [];
  const mantidos = crm.mantidos || [];
  const pasta = crm.pasta;
  const simulado = teste || crm.simulado;
  if (!preenchidos.length && !mantidos.length && !pasta && !(crm.avisos || []).length) return null;
  return (
    <Box sx={{ mt: 1, pt: 1, borderTop: `1px dashed ${cores.borda}`, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: cores.texto3 }}>
        {simulado ? 'No CRM, se não fosse teste' : 'No CRM'}
      </Typography>
      {preenchidos.length > 0 && (
        <Linha icone={<FiCheck size={12} />} cor={cores.verde}>
          {simulado ? 'Preencheria' : 'Preencheu'} {preenchidos.map((p) => p.rotulo).join(', ')}
        </Linha>
      )}
      {mantidos.map((m, k) => (
        <Linha key={k} icone={<FiAlertCircle size={12} />} cor={cores.ambar}>
          <b>{m.rotulo}</b>: respondeu “{m.resposta}”, o CRM tem “{m.crm}”{simulado ? '' : ' — continua o do CRM'}
        </Linha>
      ))}
      {pasta && (
        <Linha icone={<FiFolder size={12} />} cor={cores.texto2}>
          {pasta.entrou ? (simulado ? `Entraria na pasta ${pasta.nome}` : `Entrou na pasta ${pasta.nome}`) : `Já estava na pasta ${pasta.nome}`}
        </Linha>
      )}
      {(crm.avisos || []).map((a, k) => (
        <Linha key={`a${k}`} icone={<FiAlertCircle size={12} />} cor={cores.ambar}>{a}</Linha>
      ))}
    </Box>
  );
}

/**
 * @param respostas [{rotulo, texto, tipo, anexos}]
 * @param crm       diferença gravada pelo servidor (ou null)
 * @param cores     {texto, texto2, texto3, borda, fundo, verde, ambar}
 */
export default function RespostasLista({ respostas, crm, teste = false, cores, semCrm = false }) {
  const itens = (respostas || []).filter((r) => r.texto || (r.anexos || []).length);
  return (
    <Box>
      {!itens.length && (
        <Typography sx={{ fontSize: '0.78rem', color: cores.texto3 }}>O cliente enviou o formulário sem preencher nada.</Typography>
      )}
      <Box component="dl" sx={{ m: 0, display: 'flex', flexDirection: 'column', gap: 0.9 }}>
        {itens.map((r, k) => (
          <Box key={`${r.nome}-${k}`}>
            <Typography component="dt" sx={{ fontSize: '0.68rem', fontWeight: 600, color: cores.texto3, lineHeight: 1.3 }}>
              {r.rotulo}
            </Typography>
            <Box component="dd" sx={{ m: 0 }}>
              {(r.anexos || []).length
                ? <Anexos itens={r.anexos} cores={cores} />
                : <Typography sx={{ fontSize: '0.82rem', color: cores.texto, lineHeight: 1.45, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{r.texto}</Typography>}
            </Box>
          </Box>
        ))}
      </Box>
      {!semCrm && <ResumoCrm crm={crm} teste={teste} cores={cores} />}
    </Box>
  );
}
